import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { clientMembersService } from '../api/clientMembers.service';
import { CLIENT_MEMBERS_KEYS } from './queryKeys';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import type { Member } from '../types';

/**
 * Return type for the client members query hook.
 *
 * @remarks
 * - `data` contains members for the current page, not the full list
 * - `totalCount` comes from the backend pagination metadata, not the local array length
 * - `isFetching` is true during background refetches (distinct from isLoading)
 */
export interface UseClientMembersResult {
  /** Members for the current page */
  data: Member[];
  /** Total member count from the backend (used to calculate pages) */
  totalCount: number;
  /** True only on initial load (no cached data) */
  isLoading: boolean;
  /** True if the query failed */
  isError: boolean;
  /** True during any fetch, including background refetches */
  isFetching: boolean;
  /** Manually triggers a query refetch */
  refetch: () => void;
}

export function useClientMembers(
  clientId: string,
  search: string,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
): UseClientMembersResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const currentUserId = useAuthStore((s) => s.user?.id);

  const params = {
    SearchTerm: search || undefined,
    Page: pageIndex + 1,
    PageSize: pageSize,
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: CLIENT_MEMBERS_KEYS.list(orgId, clientId, params),
    queryFn: ({ signal }) => clientMembersService.getAll(clientId, params, currentUserId, signal),
    staleTime: 60_000,
    enabled: !!orgId && !!clientId,
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.items ?? [],
    totalCount: data?.total ?? 0,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
}
